import * as GIA from "@ntj/gaia";
import { LangValues } from '@/const/Lang';

/**
 * 地図を扱うクラス
 */
export default class Map {
  /**
   * コンストラクタ
   * @param {String} mapId 地図の描画先ID
   * @param {String} language 言語コード
   * @param {String} serviceId mocha で利用するサービスID
   * @param {Object} center
   * @param {Number} zoom
   * @param {Object} menu
   * @param {Function} onAnnotationClick 注記情報をクリックした際の処理
   */
  constructor(mapId, language, serviceId, center, zoom = 9, menu,
              onAnnotationClick, ) {
    this.mapId = mapId;
    this.language = language;
    this.serviceId = serviceId;
    this.center = center;
    this.zoom = zoom;
    this.menu = menu;
    this.onAnnotationClick = onAnnotationClick;

    // 既存オブジェクトの破棄
    if (this._map) {
      this._map.destroy();
      this._map = null;
    }
    GIA.config.endpoint = process.env.API.GIA;
    this._map = new GIA.Map(serviceId, {
      target: `#${this.mapId}`, // 対象のDOM要素またはセレクタ
      center: new GIA.value.LatLng(this.center.lat, this.center.lon), // 中心の緯度経度
      zoomLevel: this.zoom, // ズームレベル
      zoomRange: new GIA.value.ZoomRange(6, 20),
      options: {
        isAnnotationEnabled: true,
        antialias: true,
        annotationOptions: {
          display: {
            mode: 'swap',
            swapOptions: {
              swapInterval: 1000,
            },
          },
          fontFamily: {
            sansSerif: ['Noto Sans JP', GIA.types.GenericFontFamily.SansSerif],
            serif: ['Noto Serif JP', GIA.types.GenericFontFamily.Serif],
            serifBold: ['Noto Serif JP', GIA.types.GenericFontFamily.Serif],
            sansSerifBold: ['Noto Sans JP', GIA.types.GenericFontFamily.SansSerif],
          },
        },
        language: LangValues.find(lang => lang.momentLang === this.language)?.code ?? 'en',
      }
    });
    // 地図上にランドマークを表示する設定
    this._map.setLandmarkCondition(
      new GIA.value.LandmarkCondition({
        visibleZoomRange: new GIA.value.ZoomRange(14, 20),
        maxZoomLevelMiniature: 14,
        alpha: 1
      })
    )
    if (this.menu) {
      // 右クリックメニューの設定
      this.setMenu(this.menu);
    }
    if (this.onAnnotationClick) {
      // 注記情報をクリックした際の処理を設定
      this._map.setAnnotationClickListener(onAnnotationClick, {
        isIconClickable: true,
        isTextClickable: true,
        isNodeOnly: {
          icon: true,
          text: true,
        }
      })
    }
  }

  /**
   * 終了処理
   */
  destroy() {
    // 既存オブジェクトの破棄
    if (!this._map) {
      return;
    }
    this._map.destroy();
    this._map = null;
  }

  /**
   * 地図の中心を取得します
   */
  getCenter() {
    const latLng = this._map.getCenter();
    return this._convertCoord(latLng);
  }

  /**
   * 現在地図が表示している領域の左上と右下を示す緯度経度のリストを取得します。
   * @param startHeight 開始位置の高さ
   * @returns {{lon: number, latMillisec: number, lonMillisec: number, lat: number}[]}
   */
  getRect(startHeight = 0) {
    const clientSize = this._map.getClientSize();
    const leftUpper = this._map.getLatLngFromClientCoord(new GIA.value.Point(0, startHeight));
    const rightLower = this._map.getLatLngFromClientCoord(new GIA.value.Point(clientSize.width, clientSize.height));
    return [this._convertCoord(leftUpper), this._convertCoord(rightLower)];
  }

  /**
   * 地図の中心を移動します
   * @param {Object} coord
   */
  panTo(coord) {
    const dest = new GIA.value.LatLng(coord.lat, coord.lon);
    this._map.setCenter(dest, true);
  }

  /**
   * 現在地図が表示している領域の左上と右下を示す緯度経度のリストを取得します。
   */
  getRect() {
    const clientSize = this._map.getClientSize();
    const leftUpper = this._map.getLatLngFromClientCoord(new GIA.value.Point(0, 0));
    const rightLower = this._map.getLatLngFromClientCoord(new GIA.value.Point(clientSize.width, clientSize.height));
    return [this._convertCoord(leftUpper), this._convertCoord(rightLower)];
  }

  /**
   * 地図のZoom値を取得します
   */
  getZoom() {
    return this._map.getZoomLevel();
  }

  /**
   * 地図をズームアップします
   */
  zoomUp() {
    this._map.zoomIn(true);
  }

  /**
   * 地図をズームダウンします
   */
  zoomDown() {
    this._map.zoomOut(true);
  }

  /**
   * 与えられた緯度経度の配列を含むように縮尺と中心を調整します
   * @param {LatLngRect} path 緯度経度情報
   */
  setFocus(path) {
    this._map.moveBasedOnLatLngRect(path, true);
  }

  /**
   * 地図の表示サイズの変更を行います。
   */
  resize(width, height) {
    this._map?.setClientSize(new GIA.value.Size(height, width));
  }

  /**
   * 右クリックメニューを追加します。
   */
  setMenu(menu) {
    if (!menu) return;
    this._map.setContextMenu(menu);
  }

  setCenterOffset(offset) {
    this._map.setCenterOffset(new GIA.value.Point(offset.x, offset.y));
  }

  _convertCoord(latLng) {
    return {
      lat: latLng.lat,
      lon: latLng.lng,
      latMillisec: latLng.latMillisec,
      lonMillisec: latLng.lngMillisec,
    }
  }
}
